import React, { useState } from 'react';
import { Sparkles, RotateCcw, Trash2, Loader2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';

const ENHANCE_PROMPT = `Edit this sim racing rig photograph so it fits a premium dark-themed sim racing website.
Keep the rig, cockpit, steering wheel, pedals and all hardware in the same position and clearly recognizable — do not move, add or remove equipment.
Enhance: deep blacks with punchy contrast, balanced exposure, crisp clarity and sharpness, subtle red/amber accent lighting to match the brand, tidy visible cables, remove distracting background clutter, straighten and center the composition.
Finish: cinematic professional product photography look. Do NOT add text, watermarks, people or new objects.`;

/**
 * Thumbnail with an AI "enhance for website" action and a revert-to-original control.
 * Props:
 *  - src: current image URL
 *  - onChange(newUrl): called when the image is enhanced or reverted (parent updates its state)
 *  - onDelete?: optional remove handler — renders a small trash button
 *  - className: thumbnail sizing (default w-16 h-16)
 *  - alt?: image alt text
 */
export default function EnhanceableImage({ src, onChange, onDelete, className = 'w-16 h-16', alt = '' }) {
  const [original, setOriginal] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [error, setError] = useState(false);

  const isEnhanced = !!original && original !== src;

  const handleEnhance = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!src || enhancing) return;
    setEnhancing(true);
    setError(false);
    try {
      if (!original) setOriginal(src);
      const res = await base44.integrations.Core.GenerateImage({
        prompt: ENHANCE_PROMPT,
        existing_image_urls: [src],
      });
      if (res?.url) onChange(res.url);
      else setError(true);
    } catch {
      setError(true);
    } finally {
      setEnhancing(false);
    }
  };

  const handleRevert = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (original) {
      onChange(original);
      setOriginal(null);
    }
  };

  return (
    <div className={`relative ${className} bg-[#0d0d0f] border ${isEnhanced ? 'border-primary/40' : 'border-white/10'} overflow-hidden group shrink-0`}>
      {src ? <img src={src} alt={alt} className="w-full h-full object-cover" /> : null}

      {enhancing && (
        <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-1">
          <Loader2 className="w-4 h-4 text-primary animate-spin" />
          <span className="text-primary text-[8px] font-bold tracking-widest">AI…</span>
        </div>
      )}

      {error && (
        <div className="absolute top-0 left-0 bg-red-500/90 text-white text-[8px] font-bold px-1 leading-tight">ERR</div>
      )}

      {!enhancing && (
        <div className="absolute bottom-0 inset-x-0 bg-black/80 opacity-0 group-hover:opacity-100 flex justify-center gap-0.5 transition-opacity">
          <button type="button" onClick={handleEnhance} title="AI enhance for website" className="p-1.5 text-primary hover:bg-primary/20 transition-colors">
            <Sparkles className="w-3 h-3" />
          </button>
          {isEnhanced && (
            <button type="button" onClick={handleRevert} title="Revert to original" className="p-1.5 text-white/70 hover:bg-white/20 transition-colors">
              <RotateCcw className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {onDelete && !enhancing && (
        <button type="button" onClick={onDelete} title="Remove photo" className="absolute top-0 right-0 bg-black/70 opacity-0 group-hover:opacity-100 p-1 text-white/70 hover:text-red-400 transition-colors">
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}